import { computed, ref } from 'vue'
import { useEmployeeStore } from '@/stores/employee'
import type { employee, PaginationOptions } from '@/types/employee'

export function useEmployees() {
  const employeeStore = useEmployeeStore()
  
  // Pagination state
  const currentPage = ref(1)
  const itemsPerPage = ref(10)
  
  // Computed properties
  const employees = computed(() => employeeStore.filteredEmployees)
  const isLoading = computed(() => employeeStore.isLoading)
  const error = computed(() => employeeStore.error)
  const totalEmployees = computed(() => employeeStore.totalEmployees)
  const activeEmployees = computed(() => employeeStore.activeEmployees)
  const inactiveEmployees = computed(() => employeeStore.inactiveEmployees)
  
  // Pagination computeds
  const totalPages = computed(() => {
    return Math.ceil(employees.value.length / itemsPerPage.value)
  })
  
  const paginatedEmployees = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value
    const end = start + itemsPerPage.value
    return employees.value.slice(start, end)
  })
  
  // Actions
  const fetchEmployees = async () => {
    await employeeStore.fetchEmployees()
  }
  
  const fetchEmployee = async (id: number) => {
    return await employeeStore.fetchEmployee(id)
  }
  
  const updateEmployeeStatus = async (employee: employee, status: boolean) => {
    // For now, just log - actual functionality disabled
    console.log('Update employee status - Not implemented yet', { employee, status })
    return employee
  }
  
  const deleteEmployee = async (employee: employee) => {
    // For now, just log - actual functionality disabled
    console.log('Delete employee - Not implemented yet', { employee })
    return employee
  }
  
  const sortBy = (column: string) => {
    employeeStore.setSorting(column)
  }
  
  const setSearch = (searchTerm: string) => {
    employeeStore.setFilters({ search: searchTerm })
    currentPage.value = 1 // Reset to first page when searching
  }
  
  const setStatusFilter = (status: 'Active' | 'Inactive' | '') => {
    employeeStore.setFilters({ status })
    currentPage.value = 1 // Reset to first page when filtering
  }
  
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page
    }
  }
  
  const setPagination = (options: Partial<PaginationOptions>) => {
    if (options.page !== undefined) {
      currentPage.value = options.page
    }
    if (options.itemsPerPage !== undefined) {
      itemsPerPage.value = options.itemsPerPage
      currentPage.value = 1 // Reset to first page when changing items per page
    }
  }
  
  // Pagination helpers
  const getVisiblePages = () => {
    const total = totalPages.value
    const current = currentPage.value
    
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }
    
    let start = Math.max(2, current - 1)
    let end = Math.min(total - 1, current + 1)
    
    // Adjust if we're at the start or end
    if (current <= 3) {
      end = 4
    } else if (current >= total - 2) {
      start = total - 3
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }
  
  return {
    // State
    employees,
    paginatedEmployees,
    isLoading,
    error,
    currentPage,
    itemsPerPage,
    
    // Computed
    totalEmployees,
    activeEmployees,
    inactiveEmployees,
    totalPages,
    
    // Pagination
    goToPage,
    setPagination,
    getVisiblePages,
    
    // Actions
    fetchEmployees,
    fetchEmployee,
    updateEmployeeStatus,
    deleteEmployee,
    sortBy,
    setSearch,
    setStatusFilter,
    
    // Store references for direct access if needed
    sortColumn: computed(() => employeeStore.filters.sortBy),
    sortDirection: computed(() => employeeStore.filters.sortDirection),
    searchTerm: computed(() => employeeStore.filters.search),
    statusFilter: computed(() => employeeStore.filters.status)
  }
}
